import axios from "axios";
import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import { Language } from "../../App";
import { Link } from "react-router-dom";
import { PROFILE } from "../../constants/RoutesUrl";
import ImageComponent from "../common/ImageComponent";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { authorize } from "../../shared/authorize";
import debounce from "lodash/debounce";
import { LuGraduationCap, LuUsers, LuX, LuChevronRight } from "react-icons/lu";
import "./PurchasedUsersModal.css";

const parseSpecialties = (subcategory) => {
  if (!subcategory || typeof subcategory !== "string") return [];
  return subcategory
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const PurchasedUsersModal = ({ isOpen, toggle, courseId }) => {
  const { lang } = useContext(Language);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for managing state
  const abortControllerRef = useRef(null);
  const loadingStateRef = useRef(false);
  const currentCourseIdRef = useRef(null);
  const mountedRef = useRef(true);

  // Cache per course
  const cacheRef = useRef(new Map());

  // Reset component state when modal opens or courseId changes
  const resetState = useCallback(() => {
    setUsers([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setLoading(false);
    setIsInitialized(false);
    loadingStateRef.current = false;

    // Clear cache for this course
    if (courseId) {
      cacheRef.current.delete(courseId);
    }

    // Create fresh abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, [courseId]);

  const fetchPurchasedUsers = useCallback(
    async (pageNum, isRetry = false) => {
      // Validate inputs
      if (!courseId) {
        setError("Course ID is required");
        return;
      }

      // Prevent concurrent requests
      if (loadingStateRef.current && !isRetry) {
        return;
      }

      // Don't fetch if component is unmounted
      if (!mountedRef.current) {
        return;
      }

      // Check if we have more pages to load
      if (!hasMore && pageNum > 1) {
        return;
      }

      // Check cache first
      const courseCache = cacheRef.current.get(courseId);
      if (courseCache && courseCache.has(pageNum)) {
        const cachedData = courseCache.get(pageNum);

        if (pageNum === 1) {
          setUsers(cachedData.users);
        } else {
          setUsers((prev) => [...prev, ...cachedData.users]);
        }

        setPage(pageNum + 1);
        setHasMore(cachedData.hasMore);
        setIsInitialized(true);
        return;
      }

      // Set loading state
      loadingStateRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Ensure we have fresh abort controller
        if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
          abortControllerRef.current = new AbortController();
        }

        const response = await axios.get(
          `${adminBaseURL}user/course/users/purchased/${courseId}?page=${pageNum}`,
          {
            ...authorize(),
            signal: abortControllerRef.current.signal,
            timeout: 10000,
          }
        );

        if (!mountedRef.current) {
          return;
        }

        const responseData = response.data?.data;
        const fetchedUsers = responseData?.data || [];
        const currentPage = responseData?.current_page || pageNum;
        const lastPage = responseData?.last_page || pageNum;
        const hasMorePages = currentPage < lastPage;

        // Update cache
        if (!cacheRef.current.has(courseId)) {
          cacheRef.current.set(courseId, new Map());
        }
        const currentCourseCache = cacheRef.current.get(courseId);
        currentCourseCache.set(pageNum, {
          users: fetchedUsers,
          hasMore: hasMorePages,
        });

        // Update state
        if (pageNum === 1) {
          setUsers(fetchedUsers);
        } else {
          setUsers((prev) => {
            const existingIds = new Set(prev.map((u) => u.id));
            const newUsers = fetchedUsers.filter((u) => !existingIds.has(u.id));
            return [...prev, ...newUsers];
          });
        }

        setPage(pageNum + 1);
        setHasMore(hasMorePages);
        setIsInitialized(true);
      } catch (err) {
        if (!mountedRef.current || err.name === "AbortError") {
          return;
        }

        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          lang?.error_fetching_users ||
          "Failed to fetch purchased users";

        setError(errorMessage);
        setHasMore(false);
        setIsInitialized(true);

        if (pageNum === 1) {
          toast.error(errorMessage);
        }
      } finally {
        loadingStateRef.current = false;
        setLoading(false);
      }
    },
    [courseId, hasMore, lang]
  );

  // Debounced load more to prevent rapid successive calls
  const debouncedLoadMore = useMemo(
    () =>
      debounce((pageNum) => {
        if (loadingStateRef.current) {
          return;
        }
        fetchPurchasedUsers(pageNum);
      }, 500),
    [fetchPurchasedUsers]
  );

  // Handle modal open/close and courseId changes
  useEffect(() => {
    if (isOpen && courseId) {
      if (currentCourseIdRef.current !== courseId) {
        currentCourseIdRef.current = courseId;
        resetState();

        requestAnimationFrame(() => {
          if (mountedRef.current && isOpen && courseId) {
            fetchPurchasedUsers(1);
          }
        });
      } else if (!isInitialized && !loadingStateRef.current) {
        fetchPurchasedUsers(1);
      }
    } else if (!isOpen) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      currentCourseIdRef.current = null;
    }
  }, [isOpen, courseId, resetState, fetchPurchasedUsers, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      cacheRef.current.clear();
    };
  }, []);

  // Handle retry
  const handleRetry = useCallback(() => {
    resetState();
    setTimeout(() => {
      if (mountedRef.current) {
        fetchPurchasedUsers(1, true);
      }
    }, 100);
  }, [resetState, fetchPurchasedUsers]);

  // Load more handler for InfiniteScroll
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && isInitialized) {
      debouncedLoadMore(page);
    }
  }, [loading, hasMore, isInitialized, page, debouncedLoadMore]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      zIndex={1060}
      dialogClassName="pum-modal-dialog"
      contentClassName="pum-modal-content"
      backdrop="static"
      fade={true}
    >
      {/* Sleek Custom Header */}
      <div className="pum-header">
        <div className="pum-header-left">
          <div className="pum-icon-pill">
            <LuGraduationCap size={20} />
          </div>
          <div className="pum-title-group">
            <div className="pum-title-row">
              <h5 className="pum-title">
                {lang?.users_who_purchased || "Users Who Purchased"}
              </h5>
              {users.length > 0 && (
                <span className="pum-count-pill">{users.length}</span>
              )}
            </div>
            <p className="pum-subtitle">
              {lang?.course_enrolled_users_desc || "Healthcare professionals enrolled in this course"}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="pum-close-btn"
          onClick={toggle}
          aria-label={lang?.close || "Close"}
        >
          <LuX size={17} />
        </button>
      </div>

      {/* Modal Body */}
      <ModalBody className="pum-body">
        {!isInitialized && loading ? (
          <div className="pum-loading-state">
            <div className="pum-loading-spinner" />
            <div className="pum-loading-text">
              {lang?.loading || "Loading enrolled users..."}
            </div>
          </div>
        ) : error ? (
          <div className="pum-error-state">
            <div className="pum-error-msg">{error}</div>
            <button
              type="button"
              className="pum-retry-btn"
              onClick={handleRetry}
              disabled={loading}
            >
              {loading ? (lang?.loading || "Loading...") : (lang?.retry || "Retry")}
            </button>
          </div>
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={handleLoadMore}
            hasMore={hasMore && !loading}
            loader={
              <div className="text-center py-2" key="pum-loader">
                <div className="pum-loading-spinner" style={{ width: "22px", height: "22px", margin: "0 auto" }} />
              </div>
            }
            useWindow={false}
            threshold={400}
            initialLoad={false}
          >
            {users.length > 0 ? (
              users.map((user, index) => {
                const specialties = parseSpecialties(user.subcategory);

                return (
                  <Link
                    key={`${user.id}-${index}`}
                    to={`${PROFILE}/${user.user_name || user.id}`}
                    className="pum-user-card"
                    aria-label={`View profile of ${user.name || "user"}`}
                    onClick={toggle}
                  >
                    <div className="pum-user-avatar-wrap">
                      <ImageComponent
                        src={
                          user?.image && user.image !== "null"
                            ? _imagesURL + user.image
                            : Avatar
                        }
                        compClassName="pum-user-avatar"
                        loader_height="44px"
                        loader_width="44px"
                        alt={`${user.name || "user"}'s avatar`}
                      />
                    </div>

                    <div className="pum-user-info">
                      <div className="pum-user-name-row">
                        <span className="pum-user-name">{user.name || "Unknown User"}</span>
                        {user.user_name && (
                          <span className="pum-user-handle">@{user.user_name}</span>
                        )}
                      </div>

                      {specialties.length > 0 ? (
                        <div className="pum-specialty-row">
                          {specialties.slice(0, 2).map((spec, sIdx) => (
                            <span key={sIdx} className="pum-spec-pill" title={spec}>
                              {spec}
                            </span>
                          ))}
                          {specialties.length > 2 && (
                            <span
                              className="pum-spec-pill-more"
                              title={specialties.slice(2).join(", ")}
                            >
                              +{specialties.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="pum-user-fallback-role">
                          {lang?.doctor || "Doctor"}
                        </span>
                      )}
                    </div>

                    <div className="pum-action-arrow">
                      <LuChevronRight size={18} />
                    </div>
                  </Link>
                );
              })
            ) : (
              !loading && (
                <div className="pum-empty-state">
                  <div className="pum-empty-icon">
                    <LuUsers size={24} />
                  </div>
                  <h6 className="pum-empty-title">
                    {lang?.no_users_found || "No users found"}
                  </h6>
                  <p className="pum-empty-desc">
                    {lang?.no_purchased_users_desc || "No users have enrolled in this course yet."}
                  </p>
                </div>
              )
            )}
          </InfiniteScroll>
        )}
      </ModalBody>
    </Modal>
  );
};

export default React.memo(PurchasedUsersModal);